import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { MouseEvent, ReactNode, useMemo } from 'react';

import { Icon } from '@/components/Icon/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import { elevationLevel } from '@/styles/constants/paper';

import { AccountTypeDetails, AccountTypeStatus } from '@/types/api/account';

import { getDataTestId } from '@/utils/browser';
import { renderHTMLText } from '@/utils/string';

import { FigureForAccount } from './FigureForAccount';

import {
  AccountTypeActionButton,
  AccountTypeActionContainer,
  AccountTypeCardCheckBox,
  AccountTypeCardContainer,
  AccountTypeCardContent,
  AdditionalInfoContainer,
  HighlightContainer,
} from './AccountTypeCard.styled';

type AccountTypeCardProps = {
  showCheckbox?: boolean;
  data: AccountTypeDetails;
  isSelected?: boolean;
  isDisabled?: boolean;
  warningMessage?: string | ReactNode;
  onClick?: (data: AccountTypeDetails) => void;
  onSeeDetailsClick?: (data: AccountTypeDetails) => void;
};

export function AccountTypeCard({
  data,
  isSelected,
  warningMessage,
  onClick,
  onSeeDetailsClick,
  isDisabled: isDisabledProp = false,
  showCheckbox = true,
}: Readonly<AccountTypeCardProps>) {
  const { t } = useTranslate();

  const handleDetailsClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onSeeDetailsClick?.(data);
  };

  const isDisabled = useMemo(() => {
    return (
      isDisabledProp ||
      !data.isEligible ||
      data.status !== AccountTypeStatus.Active
    );
  }, [data, isDisabledProp]);

  return (
    <AccountTypeCardContainer
      {...getDataTestId(`account-type-card-${data.id}`)}
      onClick={isDisabled ? undefined : () => onClick?.(data)}
      isSelected={isSelected}
      status={data.status}
      isDisabled={isDisabled}
      elevation={elevationLevel.s}
    >
      <AccountTypeCardContent isDisabled={isDisabled}>
        <Stack direction="row" gap={2} alignItems="flex-start">
          <Typography className="AccountTypeName" variant="h4" mb={0.5}>
            {data.name}
          </Typography>
          {data.isEligible &&
            data.status === AccountTypeStatus.Active &&
            showCheckbox && (
              <AccountTypeCardCheckBox className="AccountTypeCardCheckBox">
                {isSelected && (
                  <Icon
                    name="check-mini"
                    color={(theme: Theme) => theme.palette.primaries[500]}
                    size={24}
                  />
                )}
              </AccountTypeCardCheckBox>
            )}
        </Stack>
        <ActionBlock account={data} />
        <FigureForAccount account={data} />
        {data.highlight && (
          <HighlightContainer>
            {renderHTMLText(data.highlight)}
          </HighlightContainer>
        )}
        {data.additionalInfo && (
          <AdditionalInfoContainer>
            {renderHTMLText(data.additionalInfo)}
          </AdditionalInfoContainer>
        )}
      </AccountTypeCardContent>
      <AccountTypeActionContainer gap={1}>
        {warningMessage}
        <AccountTypeActionButton
          {...getDataTestId(`account-type-see-details-button-${data.id}`)}
          className="AccountTypeActionButton"
          onClick={handleDetailsClick}
          fullWidth
          variant="outlined"
        >
          {t('accountTypeCard.button.seeDetails')}
        </AccountTypeActionButton>
      </AccountTypeActionContainer>
    </AccountTypeCardContainer>
  );
}

function ActionBlock({ account }: { account: AccountTypeDetails }) {
  const { t } = useTranslate();
  if (account.status === AccountTypeStatus.ComingSoon) {
    return (
      <Box mb={2}>
        <Chip label={t('accountTypeCard.comingSoon')} color="primary" />
      </Box>
    );
  }

  return (
    <Typography variant="body2" mb={2}>
      {t('accountTypeCard.openToday')}
    </Typography>
  );
}
